import React, { useCallback, useMemo } from 'react';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Download } from 'lucide-react';
import { getMessageActionClassName, MESSAGE_ACTION_STYLES } from '../utils/messageActionStyles';

// Constants
const ICON_SIZES = {
  MEDIUM: 14
};

const PDF_DIMENSIONS = {
  PAGE_SIZE: 'A4',
  PADDING: 30,
  MARGIN_BOTTOM: 20,
  MARGIN_TOP: 15,
  MARGIN_VERTICAL: 10,
  MARGIN_SMALL: 5,
  MARGIN_TINY: 3
};

const FONT_SIZES = {
  TITLE: 24,
  METADATA: 12,
  CONTENT: 14,
  HEADING1: 20,
  HEADING2: 18,
  HEADING3: 16,
  TABLE_CELL: 9,
  CODE: 12,
  FOOTER: 10
};

const COLORS = {
  WHITE: '#ffffff',
  GRAY: {
    LIGHT: '#f3f4f6',
    MEDIUM: '#6b7280',
    DARK: '#374151',
    BORDER: '#d1d5db',
    BORDER_LIGHT: '#e5e7eb'
  },
  BLACK: {
    LIGHT: '#111827',
    MEDIUM: '#4b5563'
  },
  BLUE: {
    MEDIUM: '#3b82f6'
  }
};

const FONT_FAMILIES = {
  DEFAULT: 'Helvetica',
  MONOSPACE: 'Courier'
};

const BORDER_WIDTHS = {
  THICK: 2,
  THIN: 1,
  LEFT: 4
};

const SPACING = {
  PADDING_SMALL: 4,
  PADDING_MEDIUM: 6,
  PADDING_LARGE: 10,
  PADDING_XL: 15,
  MARGIN_SMALL: 3,
  MARGIN_MEDIUM: 5,
  MARGIN_LARGE: 10,
  MARGIN_XL: 15
};

const LINE_HEIGHTS = {
  NORMAL: 1.6
};

const TEXT_CONTENT = {
  TITLE: 'HDX AI Chat Response',
  FOOTER_TEXT: 'This document was generated from an HDX AI chat conversation on',
  COPYRIGHT: '© 2025 HDX AI System - All rights reserved',
  DOWNLOAD_PREFIX: 'HDX_AI_Chat_Message_',
  ERROR_MESSAGE: 'Error generating PDF. Please try again.',
  BUTTON_TITLE: 'Export to PDF'
};

const REGEX_PATTERNS = {
  BOLD_MARKERS: /(\*\*.*?\*\*)/g,
  ITALIC_MARKERS: /\*(.*?)\*/g,
  CODE_MARKERS: /`(.*?)`/g,
  TABLE_SEPARATOR: /^[\s\|\-]+$/,
  HEADING3: /^###\s*/,
  HEADING2: /^##\s*/,
  HEADING1: /^#\s*/,
  BLOCKQUOTE: /^>\s*/,
  LIST_ITEM: /^[-*]\s*/
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: COLORS.WHITE,
    padding: PDF_DIMENSIONS.PADDING,
    fontFamily: FONT_FAMILIES.DEFAULT,
  },
  header: {
    marginBottom: PDF_DIMENSIONS.MARGIN_BOTTOM,
    paddingBottom: PDF_DIMENSIONS.MARGIN_TOP,
    borderBottomWidth: BORDER_WIDTHS.THICK,
    borderBottomColor: COLORS.GRAY.BORDER_LIGHT,
  },
  title: {
    fontSize: FONT_SIZES.TITLE,
    fontWeight: 'bold',
    color: COLORS.BLACK.LIGHT,
    marginBottom: SPACING.PADDING_LARGE,
  },
  metadata: {
    fontSize: FONT_SIZES.METADATA,
    color: COLORS.GRAY.MEDIUM,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  content: {
    fontSize: FONT_SIZES.CONTENT,
    lineHeight: LINE_HEIGHTS.NORMAL,
    color: COLORS.GRAY.DARK,
  },
  paragraph: {
    marginBottom: SPACING.PADDING_LARGE,
  },
  boldText: {
    marginBottom: SPACING.PADDING_LARGE,
    fontWeight: 'bold',
  },
  heading1: {
    fontSize: FONT_SIZES.HEADING1,
    fontWeight: 'bold',
    color: COLORS.BLACK.LIGHT,
    marginTop: PDF_DIMENSIONS.MARGIN_TOP,
    marginBottom: SPACING.PADDING_LARGE,
  },
  heading2: {
    fontSize: FONT_SIZES.HEADING2,
    fontWeight: 'bold',
    color: COLORS.BLACK.LIGHT,
    marginTop: 12,
    marginBottom: 8,
  },
  heading3: {
    fontSize: FONT_SIZES.HEADING3,
    fontWeight: 'bold',
    color: COLORS.BLACK.LIGHT,
    marginTop: SPACING.PADDING_LARGE,
    marginBottom: 6,
  },
  table: {
    marginVertical: SPACING.PADDING_LARGE,
    borderWidth: BORDER_WIDTHS.THIN,
    borderColor: COLORS.GRAY.BORDER,
    backgroundColor: COLORS.WHITE,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: BORDER_WIDTHS.THIN,
    borderBottomColor: COLORS.GRAY.BORDER,
    minHeight: 20,
  },
  tableHeader: {
    backgroundColor: COLORS.GRAY.LIGHT,
    fontWeight: 'bold',
  },
  tableCell: {
    padding: SPACING.PADDING_MEDIUM,
    fontSize: FONT_SIZES.TABLE_CELL,
    borderRightWidth: BORDER_WIDTHS.THIN,
    borderRightColor: COLORS.GRAY.BORDER,
    flex: 1,
    textAlign: 'left',
  },
  code: {
    backgroundColor: COLORS.GRAY.LIGHT,
    padding: SPACING.PADDING_SMALL,
    fontSize: FONT_SIZES.CODE,
    fontFamily: FONT_FAMILIES.MONOSPACE,
    marginVertical: SPACING.MARGIN_MEDIUM,
  },
  blockquote: {
    borderLeftWidth: BORDER_WIDTHS.LEFT,
    borderLeftColor: COLORS.BLUE.MEDIUM,
    paddingLeft: PDF_DIMENSIONS.MARGIN_TOP,
    marginVertical: SPACING.PADDING_LARGE,
    fontStyle: 'italic',
    color: COLORS.BLACK.MEDIUM,
  },
  list: {
    marginVertical: SPACING.MARGIN_MEDIUM,
  },
  listItem: {
    marginBottom: SPACING.MARGIN_SMALL,
    paddingLeft: SPACING.PADDING_LARGE,
  },
  footer: {
    marginTop: 30,
    paddingTop: PDF_DIMENSIONS.MARGIN_TOP,
    borderTopWidth: BORDER_WIDTHS.THIN,
    borderTopColor: COLORS.GRAY.BORDER_LIGHT,
    fontSize: FONT_SIZES.FOOTER,
    color: COLORS.GRAY.MEDIUM,
  },
});

// Function to render text with mixed formatting
const renderMixedText = (text, key) => {
  // Split text by bold markers and create Text elements
  const parts = text.split(REGEX_PATTERNS.BOLD_MARKERS);
  
  return (
    <Text style={styles.paragraph} key={key}>
      {parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          // This is bold text
          const boldContent = part.slice(2, -2);
          return (
            <Text key={index} style={styles.boldText}>
              {boldContent}
            </Text>
          );
        } else {
          // Regular text
          return part;
        }
      })}
    </Text>
  );
};

// Function to parse markdown and convert to PDF elements
const parseMarkdownToPDF = (markdown) => {
  const lines = markdown.split('\n');
  const elements = [];
  let currentTable = null;
  let tableHeaders = null;
  let inCodeBlock = false;
  let codeContent = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Handle code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <Text style={styles.code} key={`code-${i}`}>
            {codeContent}
          </Text>
        );
        codeContent = '';
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeContent += line + '\n';
      continue;
    }

    // Handle tables - improved detection
    if (line.includes('|') && line.length > 0) {
      // Skip table separator lines (lines with only |, -, and spaces)
      if (line.match(REGEX_PATTERNS.TABLE_SEPARATOR)) {
        continue;
      }
      
      // Split by | and clean up cells
      const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
      
      if (cells.length > 1) { // Only process if we have multiple cells
        if (!currentTable) {
          currentTable = [];
          tableHeaders = cells;
        } else {
          currentTable.push(cells);
        }
        continue;
      }
    }
    
    // End table if we encounter a non-table line
    if (currentTable) {
      elements.push(
        <View style={styles.table} key={`table-${i}`}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            {tableHeaders.map((header, idx) => (
              <Text style={styles.tableCell} key={idx}>
                {header}
              </Text>
            ))}
          </View>
          {currentTable.map((row, rowIdx) => (
            <View style={styles.tableRow} key={rowIdx}>
              {row.map((cell, cellIdx) => (
                <Text style={styles.tableCell} key={cellIdx}>
                  {cell}
                </Text>
              ))}
            </View>
          ))}
        </View>
      );
      currentTable = null;
      tableHeaders = null;
    }

    // Handle headings
    if (line.startsWith('###')) {
      elements.push(
        <Text style={styles.heading3} key={i}>
          {line.replace(REGEX_PATTERNS.HEADING3, '')}
        </Text>
      );
    } else if (line.startsWith('##')) {
      elements.push(
        <Text style={styles.heading2} key={i}>
          {line.replace(REGEX_PATTERNS.HEADING2, '')}
        </Text>
      );
    } else if (line.startsWith('#')) {
      elements.push(
        <Text style={styles.heading1} key={i}>
          {line.replace(REGEX_PATTERNS.HEADING1, '')}
        </Text>
      );
    }
    // Handle blockquotes
    else if (line.startsWith('>')) {
      elements.push(
        <Text style={styles.blockquote} key={i}>
          {line.replace(REGEX_PATTERNS.BLOCKQUOTE, '')}
        </Text>
      );
    }
    // Handle lists
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <Text style={styles.listItem} key={i}>
          • {line.replace(REGEX_PATTERNS.LIST_ITEM, '')}
        </Text>
      );
    }
    // Handle regular paragraphs
    else if (line) {
      // Check if line contains bold text and use mixed formatting
      if (line.includes('**')) {
        elements.push(renderMixedText(line, i));
      } else {
        // Process other inline formatting
        const processedLine = line
          .replace(REGEX_PATTERNS.ITALIC_MARKERS, '$1') // Remove italic markers
          .replace(REGEX_PATTERNS.CODE_MARKERS, '$1'); // Remove code markers
        
        elements.push(
          <Text style={styles.paragraph} key={i}>
            {processedLine}
          </Text>
        );
      }
    }
  }

  // Handle any remaining table
  if (currentTable) {
    elements.push(
      <View style={styles.table} key="final-table">
        <View style={[styles.tableRow, styles.tableHeader]}>
          {tableHeaders.map((header, idx) => (
            <Text style={styles.tableCell} key={idx}>
              {header}
            </Text>
          ))}
        </View>
        {currentTable.map((row, rowIdx) => (
          <View style={styles.tableRow} key={rowIdx}>
            {row.map((cell, cellIdx) => (
              <Text style={styles.tableCell} key={cellIdx}>
                {cell}
              </Text>
            ))}
          </View>
        ))}
      </View>
    );
  }

  return elements;
};

const PDFGenerator = ({ message, messageId, timestamp, className = "" }) => {
  // Memoized PDF elements
  const pdfElements = useMemo(() => {
    return parseMarkdownToPDF(message);
  }, [message, parseMarkdownToPDF]);

  // Memoized document component
  const MyDocument = useMemo(() => {
    return () => (
      <Document>
        <Page size={PDF_DIMENSIONS.PAGE_SIZE} style={styles.page}>
          <View style={styles.header}>
            <Text style={styles.title}>{TEXT_CONTENT.TITLE}</Text>
            <View style={styles.metadata}>
              <Text>Message ID: {messageId}</Text>
              <Text>Generated: {new Date().toLocaleString()}</Text>
            </View>
          </View>
          
          <View style={styles.content}>
            {pdfElements}
          </View>
          
          <View style={styles.footer}>
            <Text>{TEXT_CONTENT.FOOTER_TEXT} {timestamp}</Text>
            <Text>{TEXT_CONTENT.COPYRIGHT}</Text>
          </View>
        </Page>
      </Document>
    );
  }, [pdfElements, messageId, timestamp]);

  // Memoized filename generation
  const filename = useMemo(() => {
    const date = new Date().toISOString().split('T')[0];
    return `${TEXT_CONTENT.DOWNLOAD_PREFIX}${messageId}_${date}.pdf`;
  }, [messageId]);

  // Memoized event handler
  const handleGeneratePDF = useCallback(async () => {
    try {
      const blob = await pdf(<MyDocument />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(TEXT_CONTENT.ERROR_MESSAGE);
    }
  }, [MyDocument, filename]);

  return (
    <button
      onClick={handleGeneratePDF}
      className={`${getMessageActionClassName('export', false, false)} ${className}`}
      title={TEXT_CONTENT.BUTTON_TITLE}
    >
      <Download size={MESSAGE_ACTION_STYLES.ICON_SIZE} />
    </button>
  );
};

export default PDFGenerator;
